import { DeviationQuestionDto } from 'src/generated/services/TFinancialApi';

export interface QuestionFieldConfig {
  id: string;
  name: string;
  type: 'TEXT' | 'SELECT' | 'RADIO' | 'DATE' | 'DATE_RANGE' | 'DATE_TIME' | 'DATE_TIME_RANGE' | 'MULTIPLE_SELECT';
  label: string;
  required: boolean;
  options?: Array<{ id: string; label: string; disabled?: boolean }>;
  domainType?: string;
  questionId?: string; // Original question ID for TEXT fields
  nextQuestions?: DeviationQuestionDto[];
}

/**
 * Maps a DeviationQuestionDto to a simplified field configuration
 */
export const mapQuestionToFieldConfig = (question: DeviationQuestionDto): QuestionFieldConfig[] => {
  const baseConfig = {
    id: question.id || '',
    name: question.id || '',
    type: question.questionType || 'TEXT',
    label: question.name || '',
    domainType: question.questionDomainType,
    required: true,
  };

  switch (question.questionType) {
    case 'TEXT': {
      const answerOptionId = question.answerOptions?.[0]?.id || question.id || '';
      return [
        {
          ...baseConfig,
          id: answerOptionId,
          name: answerOptionId,
          label: question.name || '',
          required: false, // Text fields are typically optional
          questionId: question.id, // Store original question ID for reference
          nextQuestions: question.answerOptions?.[0]?.nextQuestions, // Use first option's next questions if any
        },
      ];
    }

    case 'DATE_RANGE':
      // For DATE_RANGE questions, create fields for each answer option
      return (
        question.answerOptions?.map((option) => ({
          ...baseConfig,
          id: option.id || '',
          name: option.id || '',
          nextQuestions: option.nextQuestions,
        })) || []
      );

    case 'RADIO':
    case 'SELECT':
    case 'MULTIPLE_SELECT':
      // For selection questions, use the question ID and map options
      return [
        {
          ...baseConfig,
          options:
            question.answerOptions?.map((option) => ({
              id: option.id || '',
              label: option.name || '',
              disabled: false,
            })) || [],
        },
      ];

    case 'DATE':
    case 'DATE_TIME':
      // For date questions, use the question ID directly
      return [baseConfig];

    default:
      console.warn(`Unsupported question type: ${question.questionType}`);
      return [];
  }
};

/**
 * Recursively processes questions and their conditional next questions
 */
export const processQuestionsRecursively = (questions: DeviationQuestionDto[], formValues: Record<string, any> = {}): QuestionFieldConfig[] => {
  const allFields: QuestionFieldConfig[] = [];
  const processedQuestions = new Set<string>();

  const processQuestion = (question: DeviationQuestionDto) => {
    if (!question.id || processedQuestions.has(question.id)) {
      return;
    }

    processedQuestions.add(question.id);
    const fieldConfigs = mapQuestionToFieldConfig(question);
    allFields.push(...fieldConfigs);

    // Check for conditional next questions
    if (question.answerOptions) {
      question.answerOptions.forEach((option) => {
        const isSelected = shouldShowNextQuestions(question, option.id || '', formValues);

        if (isSelected && option.nextQuestions) {
          option.nextQuestions.forEach((nextQuestion) => {
            processQuestion(nextQuestion);
          });
        }
      });
    }
  };

  questions.forEach(processQuestion);
  return allFields;
};

/**
 * Determines if next questions should be shown based on current form values
 */
const shouldShowNextQuestions = (question: DeviationQuestionDto, optionId: string, formValues: Record<string, any>): boolean => {
  // Special case for recurring charges - show fields immediately without requiring text input
  // This handles both the text field and the date field for recurring charges
  if (question.questionDomainType === 'WHY_DO_YOU_ACCEPT_THIS_NEW_CHARGE_AS_RECURRING' || question.questionDomainType === 'PERIOD_OF_EXPECTED_RECURRENCE') {
    return true;
  }

  switch (question.questionType) {
    case 'TEXT':
      // For text, show next questions if the field has a value
      return formValues[optionId] && formValues[optionId] !== '';

    case 'DATE':
    case 'DATE_TIME':
      // For date fields, show next questions if the field has a value
      return formValues[question.id || ''] && formValues[question.id || ''] !== '';

    case 'DATE_RANGE':
      // For date range, check both optionId and questionId since form values might use either
      return (formValues[optionId] && formValues[optionId] !== '') || (formValues[question.id || ''] && formValues[question.id || ''] !== '');

    case 'RADIO':
    case 'SELECT':
      // For radio/select, show next questions if this option is selected
      return formValues[question.id || ''] === optionId;

    case 'MULTIPLE_SELECT': {
      // For multi-select, show next questions if this option is in the array
      const selectedValues = formValues[question.id || ''] || [];
      return Array.isArray(selectedValues) && selectedValues.includes(optionId);
    }

    default:
      return false;
  }
};
